type UrlScheme = "http" | "https";

const LOCAL_API_HOST = "api.localhost.local:3000";
const LOCAL_SITE_API_HOST_SUFFIX = ".api.localhost.local:3000";
const LOCAL_APP_HOST = "localhost:3101";
const DEFAULT_SITE_API_PREFIX = "/site-api";

const envValue = (key: string): string | undefined => {
  const value = import.meta.env[key];
  return typeof value === "string" && value.trim() ? value.trim() : undefined;
};

const normalizeScheme = (value: string | undefined, fallback: UrlScheme = "http"): `${UrlScheme}://` => {
  const scheme = (value || fallback).replace(/:\/?\/?$/, "").toLowerCase();
  return scheme === "https" ? "https://" : "http://";
};

const normalizeHost = (value: string): string => (
  value
    .replace(/^https?:\/\//, "")
    .replace(/^\/\//, "")
    .replace(/\/+$/, "")
);

const normalizeHostSuffix = (value: string): string => {
  const host = normalizeHost(value);
  return host.startsWith(".") ? host : `.${host}`;
};

const normalizePath = (value: string | undefined, fallback = ""): string => {
  const path = value || fallback;
  if (!path) return "";
  return `/${path.replace(/^\/+/, "").replace(/\/+$/, "")}`;
};

const joinUrl = (baseUrl: string, path = ""): string => `${baseUrl.replace(/\/+$/, "")}${normalizePath(path)}`;

const browserLocation = (): Location | undefined => (typeof window === "undefined" ? undefined : window.location);

const apiScheme = normalizeScheme(envValue("VITE_PUBLIC_API_SCHEME"));
const accessMode = envValue("VITE_PUBLIC_ACCESS_MODE") === "ip" ? "ip" : "domain";
const apiBaseHost = normalizeHost(envValue("VITE_PUBLIC_API_BASE_HOST") || LOCAL_API_HOST);
const apiBasePath = normalizePath(envValue("VITE_PUBLIC_API_BASE_PATH"), "/api/v1");
const apiSiteHostSuffix = normalizeHostSuffix(envValue("VITE_PUBLIC_API_SITE_HOST_SUFFIX") || LOCAL_SITE_API_HOST_SUFFIX);
const apiSitePath = normalizePath(envValue("VITE_PUBLIC_API_SITE_PATH"), "/api/v1/sites");
const apiSitePrefix = normalizePath(envValue("VITE_PUBLIC_API_SITE_PREFIX"), DEFAULT_SITE_API_PREFIX);
const appScheme = normalizeScheme(envValue("VITE_PUBLIC_APP_SCHEME"), apiScheme.startsWith("https") ? "https" : "http");
const appHost = normalizeHost(envValue("VITE_PUBLIC_APP_HOST") || LOCAL_APP_HOST);
const appPort = envValue("VITE_PUBLIC_APP_PORT") || "";
const licenseScheme = normalizeScheme(
  envValue("VITE_PUBLIC_LICENSE_API_SCHEME"),
  apiScheme.startsWith("https") ? "https" : "http"
);
const licenseHost = normalizeHost(envValue("VITE_PUBLIC_LICENSE_API_HOST") || `licensing.${apiBaseHost}`);

export const publicConfig = {
  accessMode,
  apiScheme,
  apiBaseHost,
  apiBasePath,
  apiSiteHostSuffix,
  apiSitePath,
  apiSitePrefix,
  appScheme,
  appHost,
  appPort,
  licenseScheme,
  licenseHost,
  licensingEnabled: envValue("VITE_PUBLIC_LICENSING_ENABLED") !== "false",
  aiApiBaseUrl: envValue("VITE_PUBLIC_AI_API_BASE_URL") || "",
};

const browserScheme = (): `${UrlScheme}://` => normalizeScheme(browserLocation()?.protocol, "http");
const browserOrigin = (): string => browserLocation()?.origin || `${publicConfig.apiScheme}${publicConfig.apiBaseHost}`;
const browserHostname = (): string => browserLocation()?.hostname || publicConfig.apiBaseHost.split(":")[0];
const apiOrigin = (): string => (
  publicConfig.accessMode === "ip"
    ? browserOrigin()
    : `${publicConfig.apiScheme}${publicConfig.apiBaseHost}`
);

export const apiEnvironment = {
  schema: publicConfig.accessMode === "ip" ? browserScheme() : publicConfig.apiScheme,
  base: `${publicConfig.accessMode === "ip" ? browserLocation()?.host || publicConfig.apiBaseHost : publicConfig.apiBaseHost}${publicConfig.apiBasePath}`,
  site: `${publicConfig.apiSiteHostSuffix}${publicConfig.apiSitePath}`,
};

export const apiBaseUrl = (path = ""): string => joinUrl(
  `${apiOrigin()}${publicConfig.apiBasePath}`,
  path
);

export const siteApiBaseUrl = (siteSubdomain: string, path = ""): string => {
  const baseUrl = publicConfig.accessMode === "ip"
    ? `${browserOrigin()}${publicConfig.apiSitePrefix}/${encodeURIComponent(siteSubdomain)}${publicConfig.apiSitePath}`
    : `${publicConfig.apiScheme}${siteSubdomain || publicConfig.apiSiteHostSuffix.slice(1)}${siteSubdomain ? publicConfig.apiSiteHostSuffix : ""}${publicConfig.apiSitePath}`;

  return joinUrl(baseUrl, path);
};

export const apiHostUrl = (path = ""): string => joinUrl(
  apiOrigin(),
  path
);

export const uploadUrl = (uploadId: string | number): string => apiBaseUrl(`/uploads/${uploadId}`);

export const screenUrl = (siteSubdomain: string, screenSlug: string): string => {
  const defaultPort = browserScheme() === "http://" ? "80" : "443";
  const port = publicConfig.appPort && publicConfig.appPort !== defaultPort ? `:${publicConfig.appPort}` : "";
  const appOrigin = publicConfig.accessMode === "ip"
    ? `${browserScheme()}${browserHostname()}${port}`
    : `${publicConfig.appScheme}${publicConfig.appHost}`;

  return joinUrl(appOrigin, `/screen/${siteSubdomain}/${screenSlug}`);
};

export const licenseApiUrl = (path = ""): string => joinUrl(
  `${publicConfig.licenseScheme}${publicConfig.licenseHost}`,
  path
);
